/**
 * The caller's Discover layout (#2291), live: see `DiscoverLayoutStore` for
 * how it loads without a reshuffle and how writes are stored.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { getDiscoverPreferences, putDiscoverPreferences, resetDiscoverPreferences } from '@/api/discoverPreferences';
import {
  DiscoverLayoutStore,
  profileLayoutCache,
  type ApplyOptions,
  type LayoutSnapshot,
  type WriteOutcome,
} from './discoverLayoutStore';
import type { LayoutChange } from './discoverLayout';

export interface DiscoverLayoutState extends LayoutSnapshot {
  apply: (change: LayoutChange, options?: ApplyOptions) => Promise<WriteOutcome>;
  /** Back to the default preset, the saved searches kept (#2292, the server's reset since #2298). */
  reset: () => Promise<WriteOutcome>;
  /** Send changes held back for more of their kind now. */
  flush: () => void;
}

export function useDiscoverLayout(profileId: string | undefined): DiscoverLayoutState {
  const store = useMemo(
    () => new DiscoverLayoutStore({
      get: getDiscoverPreferences,
      put: putDiscoverPreferences,
      reset: resetDiscoverPreferences,
      cache: profileLayoutCache(profileId),
    }),
    [profileId],
  );
  useEffect(() => {
    store.start();
    return store.stop;
  }, [store]);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);
  return useMemo(
    () => ({ ...snapshot, apply: store.apply, reset: store.reset, flush: store.flush }),
    [snapshot, store],
  );
}
